/*
 * AWARE Hair - B2B minimum order quantity enforcement (theme-side)
 * ----------------------------------------------------------------
 * Replaces the (expired) order-limits app with an owned, no-fee rule:
 * logged-in customers tagged "b2b" must order at least N (default 3)
 * of every variant/SKU. Non-B2B and logged-out shoppers are untouched.
 *
 * Config is rendered server-side into window.AWARE_B2B by layout/theme.liquid:
 *   { isB2B: bool, minQty: number, noticeText: string }
 *
 * Enforcement surfaces (all gated on isB2B):
 *   - Product page: default the quantity to the minimum, and block
 *     add-to-cart below the minimum with a short message.
 *   - Cart page + drawer: if any non-exempt line is below the minimum,
 *     disable the checkout button(s), show a notice, and block the
 *     checkout form submit as a hard backstop.
 *
 * Escape hatch: any product/line tagged "moq-exempt" carries
 * data-b2b-exempt="true" (rendered in Liquid) and is skipped here.
 *
 * NOTE: this is client-side, so it is guidance + soft enforcement for
 * known logged-in salon accounts, not tamper-proof. Bulletproof
 * enforcement requires Shopify Plus (custom validation function) or a
 * paid app - see the implementation plan.
 */
(function () {
  'use strict';

  var cfg = window.AWARE_B2B;
  if (!cfg || !cfg.isB2B) return; // no-op for non-B2B / logged out

  var MIN = parseInt(cfg.minQty, 10);
  if (!MIN || MIN < 2) return; // nothing to enforce

  // Per-item minimum: a [data-b2b-min] override (from a "moq-min-N" product tag)
  // wins, otherwise the global MIN. Exempt items never reach this.
  function effectiveMin(el) {
    var node = el && el.closest && el.closest('[data-b2b-min]');
    if (node) {
      var m = parseInt(node.getAttribute('data-b2b-min'), 10);
      if (m >= 1) return m;
    }
    return MIN;
  }
  // Per-line message for a given minimum. Honors a settings override; if that
  // override contains "{min}" the number is substituted.
  function noticeFor(min) {
    if (cfg.noticeText) {
      return cfg.noticeText.indexOf('{min}') >= 0
        ? cfg.noticeText.replace(/\{min\}/g, min)
        : cfg.noticeText;
    }
    return 'Professional accounts: minimum ' + min + ' per item.';
  }
  // Cart-level notice is generic because lines can have different minimums.
  var CART_NOTICE = cfg.cartNotice ||
    'Some items are below the professional minimum. Adjust the highlighted items to continue.';

  var CART_ITEM = '[data-input-item]';
  var QTY = '[data-quantity-input]';
  var ATC = '[data-add-to-cart]';
  var CHECKOUT_BTNS = '.cart__submit, .quick-cart__submit, .purchase-confirmation-popup__submit';
  // Quick-add / cross-sell buttons call cart.addItemById(id, 1) in theme.js,
  // bypassing the product form entirely - they get their own capture gate.
  var QUICK_ADD = '[data-quick-shop-trigger="quick-add"], [data-add-item-id]';

  // Cart re-render observer. validateCart() mutates the cart subtree (message
  // nodes), so it must be disconnected during mutation or it self-triggers an
  // infinite loop. See reobserve()/validateCart().
  var cartObserver = null;
  var observeTargets = [];
  function reobserve() {
    if (!cartObserver) return;
    cartObserver.takeRecords();
    observeTargets.forEach(function (n) {
      cartObserver.observe(n, { childList: true, subtree: true });
    });
  }

  // ---- one-time styles (brand-aligned: gold left border, no exclamation) ----
  function injectStyles() {
    if (document.getElementById('b2b-moq-styles')) return;
    var css =
      '.b2b-moq-msg{font-family:"DM Sans",sans-serif;font-size:0.875rem;font-weight:500;' +
      'line-height:1.5;color:#102B26;background:#faf8f5;border-left:3px solid #D4A574;' +
      'padding:10px 14px;margin:10px 0 0;}' +
      '.b2b-moq-cart-msg{font-family:"DM Sans",sans-serif;font-size:0.9375rem;line-height:1.5;' +
      'color:#102B26;background:#faf8f5;border-left:3px solid #D4A574;padding:14px 16px;margin:0 0 16px;}' +
      '.b2b-moq-blocked{opacity:0.45;cursor:not-allowed;pointer-events:none;}';
    var s = document.createElement('style');
    s.id = 'b2b-moq-styles';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function isExempt(el) {
    return !!(el && el.closest && el.closest('[data-b2b-exempt="true"]'));
  }

  function setMsg(anchorEl, cls, text) {
    if (!anchorEl) return;
    var existing = anchorEl.querySelector(':scope > .' + cls);
    if (!existing) {
      existing = document.createElement('p');
      existing.className = cls;
      anchorEl.appendChild(existing);
    }
    existing.textContent = text;
    return existing;
  }

  function clearMsg(anchorEl, cls) {
    if (!anchorEl) return;
    var existing = anchorEl.querySelector(':scope > .' + cls);
    if (existing) existing.remove();
  }

  /* -------------------------------------------------------------------------
   * PRODUCT PAGE
   * ---------------------------------------------------------------------- */

  // The visible quantity selector for a given add-to-cart button. The visible
  // selector lives OUTSIDE the product form (it is a sibling within the product
  // section), so scope to the product container first, not the form.
  function pdpQtyInput(btn) {
    var scope = btn.closest('.product, [data-section-id], section') || btn.closest('form') || document;
    // a quantity input that is NOT a cart line
    var inputs = scope.querySelectorAll(QTY);
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].closest(CART_ITEM)) return inputs[i];
    }
    // fallback: the hidden form quantity field
    var form = btn.closest('form');
    return form ? form.querySelector('[name="quantity"]') : null;
  }

  function pdpReadQty(btn) {
    var input = pdpQtyInput(btn);
    var v = input ? parseInt(input.value, 10) : NaN;
    if (isNaN(v)) {
      var form = btn.closest('form');
      var hidden = form && form.querySelector('[name="quantity"]');
      v = hidden ? parseInt(hidden.value, 10) : 1;
    }
    return isNaN(v) ? 1 : v;
  }

  function pdpSetQty(btn, value) {
    var input = pdpQtyInput(btn);
    if (input) {
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var form = btn.closest('form');
    var hidden = form && form.querySelector('[name="quantity"]');
    if (hidden) hidden.value = value;
  }

  // Default each non-exempt product quantity to the minimum on load.
  // MUST go through pdpSetQty: the /cart/add payload serializes the form's
  // hidden [name=quantity], not the visible selector - setting only the
  // visible input shows 3 but adds 1.
  function primePdpDefaults() {
    document.querySelectorAll(ATC).forEach(function (btn) {
      if (btn.hasAttribute('data-b2b-exempt') || isExempt(btn)) return;
      var min = effectiveMin(btn);
      var input = pdpQtyInput(btn);
      if (input) input.setAttribute('min', String(min));
      if (pdpReadQty(btn) < min) pdpSetQty(btn, min);
    });
  }

  // Hard gate: block add-to-cart below the minimum (capture phase, before theme JS).
  function onAddToCartCapture(e) {
    var btn = e.target.closest && e.target.closest(ATC);
    if (!btn) return;
    if (btn.hasAttribute('data-b2b-exempt') || isExempt(btn)) return;
    var min = effectiveMin(btn);
    var qty = pdpReadQty(btn);
    if (qty >= min) {
      pdpSetQty(btn, qty); // valid - force hidden-field sync so payload == display
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    pdpSetQty(btn, min); // correct the value so the next click succeeds
    var anchor = btn.closest('.product-form__item--submit') || btn.parentElement;
    var msg = setMsg(anchor, 'b2b-moq-msg', noticeFor(min));
    if (msg) msg.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /* -------------------------------------------------------------------------
   * CART (page + drawer)
   * ---------------------------------------------------------------------- */

  function cartLineQty(itemEl) {
    var input = itemEl.querySelector(QTY);
    var v = input ? parseInt(input.value, 10) : NaN;
    return isNaN(v) ? 0 : v;
  }

  // Returns true if any non-exempt cart line is below the minimum.
  // Disconnects the observer during DOM mutation to avoid a self-trigger loop.
  function validateCart() {
    if (cartObserver) cartObserver.disconnect();
    var violation = false;
    try {
      document.querySelectorAll(CART_ITEM).forEach(function (itemEl) {
        if (itemEl.getAttribute('data-b2b-exempt') === 'true') return;
        var min = effectiveMin(itemEl);
        var qty = cartLineQty(itemEl);
        // floor the input's min so the stepper/native control respects it
        var input = itemEl.querySelector(QTY);
        if (input) input.setAttribute('min', String(min));
        if (qty > 0 && qty < min) {
          violation = true;
          setMsg(itemEl, 'b2b-moq-msg', noticeFor(min));
        } else {
          clearMsg(itemEl, 'b2b-moq-msg');
        }
      });
      applyCheckoutState(violation);
    } finally {
      reobserve();
    }
    return violation;
  }

  function applyCheckoutState(violation) {
    document.querySelectorAll(CHECKOUT_BTNS).forEach(function (btn) {
      if (violation) {
        btn.classList.add('b2b-moq-blocked');
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.classList.remove('b2b-moq-blocked');
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
      // cart-level notice rendered just before the checkout button group
      var holder = btn.closest('.cart__input-buttons, .quick-cart__footer-inner, .animation--purchase-confirmation-footer-item');
      if (holder) {
        if (violation) {
          var note = holder.querySelector(':scope > .b2b-moq-cart-msg');
          if (!note) {
            note = document.createElement('p');
            note.className = 'b2b-moq-cart-msg';
            note.textContent = CART_NOTICE;
            holder.insertBefore(note, holder.firstChild);
          }
        } else {
          var ex = holder.querySelector(':scope > .b2b-moq-cart-msg');
          if (ex) ex.remove();
        }
      }
    });
  }

  // Prevent the "-" stepper from dropping a line below the minimum.
  function onSubtractCapture(e) {
    var btn = e.target.closest && e.target.closest('[data-subtract-quantity]');
    if (!btn) return;
    var itemEl = btn.closest(CART_ITEM);
    if (!itemEl || itemEl.getAttribute('data-b2b-exempt') === 'true') return;
    var min = effectiveMin(itemEl);
    if (cartLineQty(itemEl) <= min) {
      e.preventDefault();
      e.stopImmediatePropagation();
      setMsg(itemEl, 'b2b-moq-msg', noticeFor(min));
    }
  }

  // Hard backstop: block any cart/drawer form submit while the cart is
  // invalid. Gating on the form (not e.submitter) covers clicked checkout,
  // Enter-key submits, AND programmatic requestSubmit()/vendor-JS submits,
  // where e.submitter is null. Update-style submits with corrected values
  // pass because validateCart() reads the live input values being submitted;
  // a zero quantity (line removal) is not a violation.
  function onSubmitCapture(e) {
    var form = e.target;
    if (!form || !form.matches) return;
    // cart page, drawer, AND the purchase-confirmation popup (its form
    // carries no cart class but posts to /cart with a checkout submit)
    var isGated = form.matches('.cart__form, .quick-cart__form')
      || !!form.querySelector('.purchase-confirmation-popup__submit');
    if (!isGated) return;
    if (validateCart()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      var note = document.querySelector('.b2b-moq-cart-msg');
      if (note) note.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  // Hard backstop: block product-form (/cart/add) submits below the minimum.
  // Catches paths that never click [data-add-to-cart]: Enter in the qty
  // field, requestSubmit()/programmatic submits, and any vendor JS submit.
  function onProductFormSubmitCapture(e) {
    var form = e.target;
    if (!form || !form.action || String(form.action).indexOf('/cart/add') === -1) return;
    var btn = form.querySelector(ATC);
    var probe = btn || form;
    if ((btn && btn.hasAttribute('data-b2b-exempt')) || isExempt(probe)) return;
    var min = effectiveMin(probe);
    var qty;
    if (btn) {
      qty = pdpReadQty(btn);
    } else {
      var hidden = form.querySelector('[name="quantity"]');
      qty = hidden ? parseInt(hidden.value, 10) : 1;
      if (isNaN(qty)) qty = 1;
    }
    if (qty >= min) {
      if (btn) pdpSetQty(btn, qty); // valid - force hidden-field sync
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    if (btn) {
      pdpSetQty(btn, min); // correct so the next attempt succeeds
      var anchor = btn.closest('.product-form__item--submit') || btn.parentElement;
      var msg = setMsg(anchor, 'b2b-moq-msg', noticeFor(min));
      if (msg) msg.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  // Quick-add / cross-sell buttons add quantity 1 directly via theme.js
  // (cart.addItemById), never touching the product form. For a non-exempt
  // product any minimum >= 2 makes a 1-unit add invalid, so block and point
  // the buyer at the product page (where the primed quantity applies).
  function onQuickAddCapture(e) {
    var btn = e.target.closest && e.target.closest(QUICK_ADD);
    if (!btn) return;
    if (btn.hasAttribute('data-b2b-exempt') || isExempt(btn)) return;
    var min = effectiveMin(btn);
    if (min < 2) return; // a min of 1 is satisfied by quick-add's qty 1
    e.preventDefault();
    e.stopImmediatePropagation();
    setMsg(btn.parentElement, 'b2b-moq-msg', noticeFor(min));
  }

  /* -------------------------------------------------------------------------
   * WIRING
   * ---------------------------------------------------------------------- */
  function init() {
    injectStyles();
    primePdpDefaults();
    validateCart();

    // Delegated, capture-phase so we run before the theme's own handlers.
    document.addEventListener('click', function (e) {
      onQuickAddCapture(e);
      if (!e.defaultPrevented) onAddToCartCapture(e);
      if (!e.defaultPrevented) onSubtractCapture(e);
    }, true);
    document.addEventListener('submit', function (e) {
      onProductFormSubmitCapture(e);
      if (!e.defaultPrevented) onSubmitCapture(e);
    }, true);

    // Re-validate whenever a cart re-renders (drawer/AJAX) or a qty changes.
    document.addEventListener('change', function (e) {
      if (e.target.closest && e.target.closest(QTY)) validateCart();
    });

    cartObserver = new MutationObserver(function () { validateCart(); });
    ['[js-cart-items]', '.quick-cart__items', '.cart__form', '.quick-cart__form', '[js-purchase-confirmation-container]'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (node) {
        if (observeTargets.indexOf(node) === -1) observeTargets.push(node);
      });
    });
    reobserve();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
